import type { CSSProperties } from "react"
import { cn } from "@/lib/utils"

interface BrandIconProps {
  size?: number
  className?: string
  /** Pass "" to mark the icon decorative when paired with a labelled wordmark. */
  alt?: string
  /**
   * "brand" (default) fills the mark with the Azure -> Violet gradient — the brand
   * / AI moment used on auth pages and AI surfaces. "neutral" fills it with the
   * foreground charcoal so it reads as a quiet monochrome mark. "filled" is the
   * app-icon badge: a white mark on a navy plate, matching the browser favicon.
   */
  tone?: "brand" | "neutral" | "filled"
}

// The mark itself is the asset's alpha channel used as a CSS mask, so the fill
// is whatever background sits behind it.
const MASK: CSSProperties = {
  WebkitMaskImage: "url(/bidcontext-logo.png)",
  maskImage: "url(/bidcontext-logo.png)",
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskPosition: "center",
  maskPosition: "center",
  WebkitMaskSize: "contain",
  maskSize: "contain",
}

// Proportions lifted straight from public/favicon.svg (rx="24" on a 100 box, mark
// inset to 66) so the in-app badge is the browser tab icon at a different scale.
const BADGE_RADIUS_RATIO = 0.24
const BADGE_MARK_RATIO = 0.66

// App brand icon mark — the interlocking Bidcontext symbol. Used for the collapsed
// sidebar and inside <BrandLockup />.
export function BrandIcon({ size = 28, className, alt = "Bidcontext", tone = "brand" }: BrandIconProps) {
  const a11y = {
    role: alt ? ("img" as const) : undefined,
    "aria-label": alt || undefined,
    "aria-hidden": alt ? undefined : true,
  }

  if (tone === "filled") {
    return (
      <span
        {...a11y}
        style={{
          height: size,
          width: size,
          borderRadius: size * BADGE_RADIUS_RATIO,
          background: "hsl(var(--brand-navy))",
        }}
        className={cn("inline-flex items-center justify-center select-none", className)}
      >
        <span
          aria-hidden
          style={{
            ...MASK,
            height: size * BADGE_MARK_RATIO,
            width: size * BADGE_MARK_RATIO,
            background: "#fff",
          }}
        />
      </span>
    )
  }

  const style: CSSProperties = {
    ...MASK,
    height: size,
    width: size,
    // Neutral: foreground charcoal — a quiet monochrome mark matching the wordmark.
    // Brand: deeper, more saturated azure -> violet than the --brand/--ai-grad-to
    // stops (those read hazy on the light sidebar). Lower lightness + higher
    // saturation makes the thin-stroke mark pop. Re-picked for the azure accent
    // (2026-08-27); it tracks --brand/--ai-grad-to at the same offsets iris used.
    background:
      tone === "neutral"
        ? "hsl(var(--foreground))"
        : "linear-gradient(135deg, hsl(216 95% 40%) 0%, hsl(260 80% 50%) 100%)",
  }

  return <span {...a11y} style={style} className={cn("inline-block select-none", className)} />
}
